import type { LevelDoc } from '../model/format.ts';
import { applyCamera, drawScene, viewRect } from '../render/renderer.ts';
import { buildScene, layerTiles } from './scene.ts';

/** World bounds of the level's tiles (solid layers first; all layers when there are none). */
export function levelBounds(doc: LevelDoc): [number, number, number, number] | null {
  const scan = (roles: Set<string> | null) => {
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const l of doc.layers) {
      if (l.visible === false || (roles && !roles.has(l.role))) continue;
      const t = layerTiles(doc, doc, l);
      for (let i = 0; i < t.length; i += 4) {
        if (t[i + 2] < 0) continue;
        if (t[i] < x0) x0 = t[i];
        if (t[i] + 1 > x1) x1 = t[i] + 1;
        if (t[i + 1] < y0) y0 = t[i + 1];
        if (t[i + 1] + 1 > y1) y1 = t[i + 1] + 1;
      }
    }
    return x0 < x1 ? ([x0, y0, x1, y1] as [number, number, number, number]) : null;
  };
  return scan(new Set(['level', 'oneway', 'spikes'])) ?? scan(null);
}

/** Small picture of the whole level, as a data URL (WebP, JPEG where WebP encoding is unsupported). */
export function renderThumbnail(doc: LevelDoc, w = 320, h = 180): string | null {
  const b = levelBounds(doc);
  if (!b || typeof document === 'undefined') return null;
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d');
  if (!ctx) return null;
  const bg = doc.settings.backgroundColor;
  ctx.fillStyle = `rgb(${bg[0] * 255},${bg[1] * 255},${bg[2] * 255})`;
  ctx.fillRect(0, 0, w, h);
  const pad = 2;
  const zoom = Math.min(w / (b[2] - b[0] + pad * 2), h / (b[3] - b[1] + pad * 2));
  const cam = { x: (b[0] + b[2]) / 2, y: (b[1] + b[3]) / 2, zoom };
  ctx.imageSmoothingEnabled = zoom < 8;
  applyCamera(ctx, cam, w, h);
  try {
    drawScene(ctx, buildScene(doc, { view: viewRect(cam, w, h), scrub: 0, focus: 'level' }));
  } catch {
    return null;
  }
  const webp = c.toDataURL('image/webp', 0.72);
  return webp.startsWith('data:image/webp') ? webp : c.toDataURL('image/jpeg', 0.75);
}
